import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Table } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useStore } from 'effector-react';
import moment from 'moment';
import IUser, { AccountChange } from '@interfaces/IUser';
import { getAccountChangeHistory } from '@stores/user';
import { unlinkDiscord } from '@stores/users';
import auth from '@stores/auth';
import useTranslate from '@hooks/useTranslate';
import { FULL_TIME_FORMAT } from '@config/format';
import componentContent from './AccountChangeHistory.content.json';
import css from './UserInfo.module.less';

const PAGE_SIZE = 20;

const AccountChangeHistory = ({ userId }: { userId: IUser['id'] }) => {
  const content = useTranslate(componentContent);
  const { user: authUser } = useStore(auth);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [history, setHistory] = useState<{
    changes: AccountChange[];
    page: number;
    total: number;
  }>({ changes: [], page: 0, total: 0 });
  const requestId = useRef(0);
  const requestedPage = useRef(0);

  const loadPage = useCallback(
    async (page: number) => {
      const currentRequest = ++requestId.current;
      requestedPage.current = page;
      setLoading(true);
      setHasError(false);
      try {
        const changes = await getAccountChangeHistory({ userId, page, pageSize: PAGE_SIZE });
        if (currentRequest !== requestId.current) return;

        const nextPage =
          changes.length === PAGE_SIZE
            ? await getAccountChangeHistory({ userId, page: (page + 1) * PAGE_SIZE, pageSize: 1 })
            : [];
        if (currentRequest !== requestId.current) return;

        if (page > 0 && changes.length === 0) {
          setHistory((previous) => ({
            ...previous,
            total: previous.page * PAGE_SIZE + previous.changes.length,
          }));
        } else {
          const knownTotal = page * PAGE_SIZE + changes.length + nextPage.length;
          setHistory((previous) => ({
            changes,
            page,
            total: nextPage.length ? Math.max(previous.total, knownTotal) : knownTotal,
          }));
        }
      } catch {
        if (currentRequest === requestId.current) setHasError(true);
      } finally {
        if (currentRequest === requestId.current) setLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    if (!visible) return;
    setHistory({ changes: [], page: 0, total: 0 });
    loadPage(0);
    const unsubscribe = unlinkDiscord.done.watch(({ params }) => {
      if (String(params.userId) === String(userId)) loadPage(0);
    });
    return () => {
      requestId.current += 1;
      unsubscribe();
    };
  }, [visible, userId, loadPage]);

  const renderValue = (value?: string | null) => value || '—';
  const columns: ColumnsType<AccountChange> = [
    {
      title: content.date,
      dataIndex: 'changedAt',
      width: 160,
      render: (value: string) =>
        value && moment(value).isValid() ? moment(value).format(FULL_TIME_FORMAT) : '—',
    },
    {
      title: content.field,
      dataIndex: 'changedField',
      width: 150,
      render: (value: string) => (value === 'SERVICES.DISCORD' ? 'Discord' : renderValue(value)),
    },
    { title: content.previousValue, dataIndex: 'prevValue', width: 160, render: renderValue },
    { title: content.currentValue, dataIndex: 'newValue', width: 160, render: renderValue },
    {
      title: content.responsible,
      key: 'responsible',
      width: 200,
      render: (_, change) => {
        const responsible = change.changedBy;
        if (!responsible) return '—';
        if (String(responsible.id) === String(change.accountId)) return content.user;
        return (
          responsible.email ||
          (String(responsible.id) === String(authUser?.id) ? authUser?.email : undefined) ||
          responsible.nickname ||
          String(responsible.id)
        );
      },
    },
    { title: content.comment, dataIndex: 'comment', width: 220, render: renderValue },
  ];

  return (
    <>
      <Button
        icon={<HistoryOutlined />}
        onClick={() => setVisible(true)}
      >
        {content.title}
      </Button>
      <Modal
        title={content.title}
        visible={visible}
        onCancel={() => setVisible(false)}
        width={1100}
        footer={null}
      >
        {hasError && (
          <Alert
            type='error'
            showIcon
            message={content.loadError}
            action={
              <Button onClick={() => loadPage(requestedPage.current)}>{content.retry}</Button>
            }
          />
        )}
        <Table<AccountChange>
          className={css.accountHistoryTable}
          columns={columns}
          dataSource={history.changes}
          rowKey='id'
          loading={loading}
          pagination={{
            current: history.page + 1,
            pageSize: PAGE_SIZE,
            total: history.total,
            onChange: (page) => loadPage(page - 1),
            position: ['topRight'],
            size: 'small',
            showSizeChanger: false,
            disabled: loading,
          }}
          size='small'
          scroll={{ x: 1050 }}
          locale={{ emptyText: hasError ? content.loadError : content.empty }}
        />
      </Modal>
    </>
  );
};

export default AccountChangeHistory;
